/**
 * uploadFileInfoBuilder
 *
 * Mirrors Flutter's `VideoProcessService._getUploadInfo`
 * (lib/services/video_process_service.dart:10). Returns the MIME content type
 * and extension for the video file and packages it together with the upload
 * path / filename used by Firebase Storage.
 *
 * Flutter accepts only mp4/mov on mobile. Web accepts a superset of video
 * MIME types (see `videoFormatUtils.ts`) so we enumerate the mapping here.
 */

import type { UploadFileInfo } from '@/domain/entities/publish/uploadFileInfo';
import {
  EXTENSION_TO_CONTENT_TYPE,
  IMAGE_EXTENSION_TO_CONTENT_TYPE,
  IMAGE_MIME_TO_CONTENT_TYPE,
  MIME_TO_CONTENT_TYPE,
  type VideoContentTypeInfo,
} from '@/domain/utils/videoFormatUtils';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';

interface BuildUploadFileInfoParams {
  file: File;
  firebaseUserId: string;
  durationSeconds: number;
  typeLimit?: number;
}

const getFileExtension = (file: File): string | null => {
  const dotIndex = file.name.lastIndexOf('.');
  if (dotIndex < 0) {
    return null;
  }
  return file.name.slice(dotIndex + 1).toLowerCase();
};

/**
 * Resolve the content-type/extension for an image input. The chat content flow
 * accepts images as well as videos (Flutter fires `transcodeChatImage` for
 * `media.type == 0`). Mirrors the video branch: prefer MIME, fall back to the
 * file extension, then fall back to jpeg for any unknown image.
 *
 * Deliberate web divergence: Flutter forces `image/jpeg` / `jpg` for ALL images
 * (`lib/services/video_process_service.dart:17-20`); web preserves the source
 * format by design (same web-superset rationale already documented for the
 * video branch). This is intentional and is NOT a parity violation.
 */
const resolveImageContentType = (file: File, mime: string | undefined): VideoContentTypeInfo => {
  if (mime && IMAGE_MIME_TO_CONTENT_TYPE[mime]) {
    return IMAGE_MIME_TO_CONTENT_TYPE[mime];
  }

  const ext = getFileExtension(file);
  if (ext && IMAGE_EXTENSION_TO_CONTENT_TYPE[ext]) {
    return IMAGE_EXTENSION_TO_CONTENT_TYPE[ext];
  }

  // Fallback to jpeg for any unrecognised image (mirrors the mp4 video fallback).
  return IMAGE_MIME_TO_CONTENT_TYPE['image/jpeg'];
};

const resolveContentType = (file: File): VideoContentTypeInfo => {
  const mime = file.type?.toLowerCase();

  // Image inputs (chat content) resolve to an image content-type/extension.
  const ext = getFileExtension(file) ?? '';
  if (mime?.startsWith('image/') || IMAGE_EXTENSION_TO_CONTENT_TYPE[ext]) {
    return resolveImageContentType(file, mime);
  }

  if (mime && MIME_TO_CONTENT_TYPE[mime]) {
    return MIME_TO_CONTENT_TYPE[mime];
  }

  if (ext && EXTENSION_TO_CONTENT_TYPE[ext]) {
    return EXTENSION_TO_CONTENT_TYPE[ext];
  }
  // Flutter's fallback is mp4 when the extension is anything other than .mov.
  return MIME_TO_CONTENT_TYPE['video/mp4'];
};

export const buildUploadFileInfo = ({
  file,
  firebaseUserId,
  durationSeconds,
  // Default parameter expressions are evaluated per call, so this reads the
  // live Remote Config value at call time rather than at module load.
  typeLimit = remoteConfigService.contentTypeLimit,
}: BuildUploadFileInfoParams): UploadFileInfo => {
  const { contentType, fileExtension } = resolveContentType(file);
  return {
    file,
    uploadFilePath: firebaseUserId,
    uploadFileName: Date.now().toString(),
    contentType,
    fileExtension,
    durationMs: Math.round(durationSeconds * 1000),
    typeLimit,
  };
};
